import { Check, X } from 'lucide-react';
import { Text } from '@/components/ui/Text';
import type { RegisterPolicy } from '@/lib/auth/register-api';

interface PasswordPolicyHintProps {
  policy: RegisterPolicy['passwordPolicy'];
  value: string;
  /** When false, rules show as neutral hints (•) — avoids "all failing" appearance on an untouched field. */
  touched: boolean;
}

interface Rule {
  label: string;
  met: (pw: string) => boolean;
}

function buildRules(policy: RegisterPolicy['passwordPolicy']): Rule[] {
  const rules: Rule[] = [
    { label: `At least ${policy.minLength} characters`, met: (pw) => pw.length >= policy.minLength },
  ];
  if (policy.requireUppercase) {
    rules.push({ label: 'One uppercase letter', met: (pw) => /[A-Z]/.test(pw) });
  }
  if (policy.requireLowercase) {
    rules.push({ label: 'One lowercase letter', met: (pw) => /[a-z]/.test(pw) });
  }
  if (policy.requireDigit) {
    rules.push({ label: 'One number', met: (pw) => /[0-9]/.test(pw) });
  }
  if (policy.requireSpecial) {
    rules.push({ label: 'One special character', met: (pw) => /[^A-Za-z0-9]/.test(pw) });
  }
  return rules;
}

/** Inline password rule checklist shown before the password field — rules sourced from policy. */
export function PasswordPolicyHint({ policy, value, touched }: PasswordPolicyHintProps) {
  const rules = buildRules(policy);

  return (
    <ul className="flex flex-col gap-1 py-1" aria-label="Password requirements">
      {rules.map((rule) => {
        const met = value.length > 0 && rule.met(value);
        const neutral = !touched;
        return (
          <li key={rule.label} className="flex items-center gap-2">
            {neutral ? (
              <span className="h-3.5 w-3.5 shrink-0 flex items-center justify-center text-text-subtle text-xs leading-none" aria-hidden>
                •
              </span>
            ) : met ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
            ) : (
              <X className="h-3.5 w-3.5 shrink-0 text-danger" aria-hidden />
            )}
            <Text variant="body-sm" color={neutral ? 'subtle' : met ? 'success' : 'danger'}>
              {rule.label}
            </Text>
          </li>
        );
      })}
    </ul>
  );
}
